import React, { useEffect, useRef, useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Chip,
  CircularProgress, Button, Alert, Avatar, Divider,
  IconButton, Tooltip,
} from '@mui/material';
import {
  Satellite, Person, Refresh, MyLocation,
  Speed, Height, AccessTime, Public, WbSunny, NightsStay,
  SignalCellularAlt, Fullscreen, FullscreenExit,
} from '@mui/icons-material';
import { issAPI } from '../services/api';

// ─── ISS SVG Icon ─────────────────────────────────────────────────────────────
const ISS_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80" width="80" height="80">
  <!-- Main truss -->
  <rect x="5" y="37" width="70" height="6" rx="2" fill="#c8d8e8" stroke="#7aa0c0" stroke-width="0.5"/>
  <!-- Central module -->
  <rect x="30" y="28" width="20" height="24" rx="3" fill="#d0dce8" stroke="#7aa0c0" stroke-width="0.8"/>
  <!-- Front module -->
  <rect x="24" y="32" width="12" height="16" rx="2" fill="#b8ccd8" stroke="#7aa0c0" stroke-width="0.5"/>
  <!-- Back module -->
  <rect x="44" y="32" width="12" height="16" rx="2" fill="#b8ccd8" stroke="#7aa0c0" stroke-width="0.5"/>
  <!-- Solar panel left top -->
  <rect x="5" y="22" width="22" height="10" rx="1" fill="#1a3a6a" stroke="#ffffff" stroke-width="0.8" opacity="0.9"/>
  <!-- Solar panel left bottom -->
  <rect x="5" y="48" width="22" height="10" rx="1" fill="#1a3a6a" stroke="#ffffff" stroke-width="0.8" opacity="0.9"/>
  <!-- Solar panel right top -->
  <rect x="53" y="22" width="22" height="10" rx="1" fill="#1a3a6a" stroke="#ffffff" stroke-width="0.8" opacity="0.9"/>
  <!-- Solar panel right bottom -->
  <rect x="53" y="48" width="22" height="10" rx="1" fill="#1a3a6a" stroke="#ffffff" stroke-width="0.8" opacity="0.9"/>
  <!-- Solar cell lines left top -->
  <line x1="11" y1="22" x2="11" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="17" y1="22" x2="17" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="23" y1="22" x2="23" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <!-- Solar cell lines left bottom -->
  <line x1="11" y1="48" x2="11" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="17" y1="48" x2="17" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="23" y1="48" x2="23" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <!-- Solar cell lines right top -->
  <line x1="59" y1="22" x2="59" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="65" y1="22" x2="65" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="71" y1="22" x2="71" y2="32" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <!-- Solar cell lines right bottom -->
  <line x1="59" y1="48" x2="59" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="65" y1="48" x2="65" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <line x1="71" y1="48" x2="71" y2="58" stroke="#ffffff" stroke-width="0.4" opacity="0.6"/>
  <!-- Glow dot center -->
  <circle cx="40" cy="40" r="3" fill="#ffffff" opacity="0.9"/>
  <circle cx="40" cy="40" r="6" fill="#ffffff" opacity="0.2"/>
</svg>
`;

// ─── Leaflet CSS injection ────────────────────────────────────────────────────
const LEAFLET_CSS = `
.leaflet-container { background: #000000 !important; font-family: inherit; }
:-webkit-full-screen .leaflet-container { height: 100vh !important; width: 100vw !important; }
:-moz-full-screen .leaflet-container { height: 100vh !important; width: 100vw !important; }
:fullscreen .leaflet-container { height: 100vh !important; width: 100vw !important; }
:-webkit-full-screen { background: #000000; }
:fullscreen { background: #000000; }
.leaflet-tile-pane { filter: brightness(0.85) saturate(0.7) hue-rotate(200deg); }
.leaflet-control-zoom a { background: rgba(10,22,40,0.9) !important; color: #ffffff !important; border-color: rgba(255,255,255,0.3) !important; }
.leaflet-control-zoom a:hover { background: rgba(2,136,209,0.4) !important; }
.leaflet-control-attribution { background: rgba(5,13,26,0.7) !important; color: rgba(255,255,255,0.4) !important; font-size: 9px; }
.leaflet-control-attribution a { color: rgba(255,255,255,0.5) !important; }
.iss-marker-container { filter: drop-shadow(0 0 8px #ffffff); }
.iss-pulse { animation: iss-pulse-anim 2s infinite; }
@keyframes iss-pulse-anim { 0%,100% { opacity:1; transform: scale(1); } 50% { opacity:0.7; transform: scale(1.05); } }
.orbit-path { animation: orbit-dash 20s linear infinite; }
`;

export default function LiveISSPage() {
  const mapRef        = useRef(null);
  const mapInstance   = useRef(null);
  const issMarker     = useRef(null);
  const orbitPolyline = useRef(null);
  const leafletRef    = useRef(null);
  const intervalRef   = useRef(null);
  const orbitPoints   = useRef([]);

  const [position, setPosition]       = useState(null);
  const [astronauts, setAstronauts]   = useState([]);
  const [passes, setPasses]           = useState([]);
  const [loading, setLoading]         = useState(true);
  const [userLocation, setUserLocation] = useState(null);
  const [lastUpdate, setLastUpdate]   = useState(null);
  const [mapLoaded, setMapLoaded]     = useState(false);
  const [isDaytime,    setIsDaytime]    = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  // ── invalidateSize after fullscreen toggle ──────────────────────────────────
  useEffect(() => {
    if (!mapInstance.current) return;
    const timer = setTimeout(() => mapInstance.current.invalidateSize(), 150);
    return () => clearTimeout(timer);
  }, [isFullscreen]);

  // ── ESC key to exit fullscreen ──────────────────────────────────────────────
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && document.fullscreenElement) document.exitFullscreen?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const [heading, setHeading]         = useState(0);
  const prevPos                       = useRef(null);

  // ── Inject Leaflet CSS ──────────────────────────────────────────────────────
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = LEAFLET_CSS;
    document.head.appendChild(style);
    return () => document.head.removeChild(style);
  }, []);

  // ── Load Leaflet dynamically ────────────────────────────────────────────────
  useEffect(() => {
    const loadLeaflet = async () => {
      if (window.L) { leafletRef.current = window.L; initMap(); return; }

      const link  = document.createElement('link');
      link.rel    = 'stylesheet';
      link.href   = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.css';
      document.head.appendChild(link);

      const script = document.createElement('script');
      script.src   = 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.min.js';
      script.onload = () => { leafletRef.current = window.L; initMap(); };
      document.head.appendChild(script);
    };
    loadLeaflet();
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, []);

  // ── Init Map ────────────────────────────────────────────────────────────────
  const initMap = useCallback(() => {
    if (!mapRef.current || mapInstance.current) return;
    const L = leafletRef.current;

    const map = L.map(mapRef.current, {
      center: [20, 0], zoom: 2,
      zoomControl: true,
      attributionControl: true,
      minZoom: 1, maxZoom: 8,
    });

    // Dark tile layer
    L.tileLayer(
      'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
      { attribution: '© OpenStreetMap © CARTO', subdomains: 'abcd', maxZoom: 8 }
    ).addTo(map);

    // ISS custom icon
    const issIcon = L.divIcon({
      html: `<div class="iss-marker-container iss-pulse">${ISS_SVG}</div>`,
      iconSize:   [80, 80],
      iconAnchor: [40, 40],
      className:  '',
    });

    // ISS marker
    issMarker.current = L.marker([20, 0], { icon: issIcon, zIndexOffset: 1000 }).addTo(map);
    issMarker.current.bindPopup(`
      <div style="background:#000000;color:#ffffff;padding:10px;border-radius:8px;min-width:160px;border:1px solid rgba(255,255,255,0.3)">
        <div style="font-weight:800;font-size:14px;margin-bottom:6px">🛸 ISS</div>
        <div style="font-size:11px;color:#90caf9">International Space Station</div>
        <div style="font-size:11px;color:#90caf9;margin-top:4px">Altitude: ~408 km</div>
        <div style="font-size:11px;color:#90caf9">Speed: 7.66 km/s</div>
      </div>
    `, { className: 'iss-popup' });

    // Orbit polyline
    orbitPolyline.current = L.polyline([], {
      color: '#ffffff', weight: 2, opacity: 0.6,
      dashArray: '6 4',
    }).addTo(map);

    mapInstance.current = map;
    setMapLoaded(true);
  }, []);

  // ── Update ISS on map ───────────────────────────────────────────────────────
  const updateMapISS = useCallback((lat, lon) => {
    const L = leafletRef.current;
    if (!L || !issMarker.current || !mapInstance.current) return;

    // Calculate heading
    if (prevPos.current) {
      const dLon = lon - prevPos.current.lon;
      const dLat = lat - prevPos.current.lat;
      const h    = Math.atan2(dLon, dLat) * (180 / Math.PI);
      setHeading(h);

      // Rotate ISS icon
      const container = issMarker.current.getElement();
      if (container) {
        const inner = container.querySelector('.iss-marker-container');
        if (inner) inner.style.transform = `rotate(${h}deg)`;
      }
    }
    prevPos.current = { lat, lon };

    // Animate marker to new position
    issMarker.current.setLatLng([lat, lon]);

    // Update orbit trail
    orbitPoints.current.push([lat, lon]);
    if (orbitPoints.current.length > 50) orbitPoints.current.shift();

    // Handle antimeridian crossing — split path if needed
    const segments = [];
    let seg = [orbitPoints.current[0]];
    for (let i = 1; i < orbitPoints.current.length; i++) {
      const prev = orbitPoints.current[i - 1];
      const curr = orbitPoints.current[i];
      if (Math.abs(curr[1] - prev[1]) > 180) {
        segments.push(seg);
        seg = [curr];
      } else {
        seg.push(curr);
      }
    }
    segments.push(seg);
    orbitPolyline.current.setLatLngs(segments);

    // Determine day/night (rough: sun position vs ISS longitude)
    const sunLon = ((Date.now() / 86400000) % 1) * 360 - 180;
    const diff   = Math.abs(lon - sunLon);
    setIsDaytime(diff < 90 || diff > 270);
  }, []);

  // ── Fetch ISS position ──────────────────────────────────────────────────────
  const fetchPosition = useCallback(async () => {
    try {
      const res  = await issAPI.getPosition();
      const data = res.data;
      if (data.iss_position) {
        const lat = parseFloat(data.iss_position.latitude);
        const lon = parseFloat(data.iss_position.longitude);
        const newPos = { lat, lon, timestamp: data.timestamp };
        setPosition(newPos);
        setLastUpdate(new Date());
        updateMapISS(lat, lon);
      }
    } catch (err) {
      console.error('ISS fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [updateMapISS]);

  // ── Fetch astronauts ────────────────────────────────────────────────────────
  const fetchAstronauts = useCallback(async () => {
    try {
      const res = await issAPI.getAstronauts();
      if (res.data.people) setAstronauts(res.data.people);
    } catch (err) { console.error(err); }
  }, []);

  // ── Fetch passes ────────────────────────────────────────────────────────────
  const fetchPasses = useCallback(async (lat, lon) => {
    try {
      const res = await issAPI.getPasses(lat, lon);
      if (res.data.response) setPasses(res.data.response.slice(0, 5));
    } catch (err) { console.error(err); }
  }, []);

  // ── User location ───────────────────────────────────────────────────────────
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        pos => {
          const loc = { lat: pos.coords.latitude, lon: pos.coords.longitude };
          setUserLocation(loc);
          fetchPasses(loc.lat, loc.lon);
          // Add user marker when map is ready
          if (mapInstance.current && leafletRef.current) {
            const L = leafletRef.current;
            const userIcon = L.divIcon({
              html: `<div style="width:14px;height:14px;border-radius:50%;background:#66bb6a;border:2px solid #fff;box-shadow:0 0 10px #66bb6a"></div>`,
              iconSize: [14, 14], iconAnchor: [7, 7], className: '',
            });
            L.marker([loc.lat, loc.lon], { icon: userIcon })
              .addTo(mapInstance.current)
              .bindPopup('<div style="color:#66bb6a;font-weight:700;background:#000000;padding:6px 10px;border-radius:6px">📍 Your Location</div>');
          }
        },
        () => {}
      );
    }
  }, [fetchPasses, mapLoaded]);

  // ── Start live tracking ─────────────────────────────────────────────────────
  useEffect(() => {
    if (!mapLoaded) return;
    fetchPosition();
    fetchAstronauts();
    intervalRef.current = setInterval(fetchPosition, 3000);
    return () => clearInterval(intervalRef.current);
  }, [mapLoaded, fetchPosition, fetchAstronauts]);

  // ── Center map on ISS ───────────────────────────────────────────────────────
  const centerOnISS = () => {
    if (mapInstance.current && position) {
      mapInstance.current.setView([position.lat, position.lon], 4, { animate: true });
    }
  };

  const issAstronauts = astronauts.filter(a => a.craft === 'ISS');
  const otherCraft    = [...new Set(astronauts.filter(a => a.craft !== 'ISS').map(a => a.craft))];

  const ASTRONAUT_COLORS = [
    '#ffffff','#66bb6a','#ffa726','#ab47bc',
    '#ef5350','#26c6da','#ffd600','#ff7043',
  ];

  return (
    <Box sx={{ minHeight: '100vh' }}>

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2, flexWrap:'wrap', gap:1 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight:800 }}>
            🛸 Live ISS Tracker
          </Typography>
          <Typography variant="body2" sx={{ color:'text.secondary', mt:0.5 }}>
            Real-time position on interactive world map • Updates every 3 seconds
          </Typography>
        </Box>
        <Box sx={{ display:'flex', gap:1.5, alignItems:'center', flexWrap:'wrap' }}>
          {lastUpdate && (
            <Typography variant="caption" sx={{ color:'text.secondary' }}>
              Updated: {lastUpdate.toLocaleTimeString()}
            </Typography>
          )}
          <Chip
            label={isDaytime ? '☀ In Sunlight' : '🌑 In Shadow'}
            size="small"
            sx={{
              bgcolor: isDaytime ? 'rgba(255,214,0,0.15)' : 'rgba(255,255,255,0.15)',
              color:   isDaytime ? '#ffd600' : 'primary.light',
              fontWeight: 700, border: isDaytime ? '1px solid rgba(255,214,0,0.4)' : '1px solid rgba(255,255,255,0.4)',
            }}
          />
          <Chip
            label="● LIVE"
            size="small"
            sx={{
              bgcolor: 'rgba(102,187,106,0.15)', color:'#66bb6a',
              border:'1px solid rgba(102,187,106,0.4)', fontWeight:800,
              animation:'pulse 2s infinite',
              '@keyframes pulse':{ '0%,100%':{ opacity:1 }, '50%':{ opacity:0.5 } },
            }}
          />
          <Button size="small" variant="outlined" startIcon={<MyLocation />}
            onClick={centerOnISS}
            sx={{ borderColor:'rgba(255,255,255,0.4)', color:'primary.light' }}>
            Track ISS
          </Button>
                <Button size="small" variant="outlined" startIcon={<Refresh />}
            onClick={fetchPosition}
            sx={{ borderColor:'rgba(255,255,255,0.4)', color:'primary.light' }}>
            Refresh
          </Button>
        </Box>
      </Box>

      {/* ── Live Stats ─────────────────────────────────────────────────────── */}
      <Grid container spacing={1.5} sx={{ mb:2 }}>
        {[
          { icon:<MyLocation />,    label:'Latitude',    value: position ? `${position.lat.toFixed(4)}°` : '---',  color:'#ffffff' },
          { icon:<Public />,        label:'Longitude',   value: position ? `${position.lon.toFixed(4)}°` : '---',  color:'#ffffff' },
          { icon:<Height />,        label:'Altitude',    value:'408 km',                                           color:'#66bb6a' },
          { icon:<Speed />,         label:'Speed',       value:'27,600 km/h',                                      color:'#ffa726' },
          { icon:<AccessTime />,    label:'Orbit Period',value:'92 min',                                           color:'#ab47bc' },
          { icon:<Person />,        label:'Crew aboard', value:`${issAstronauts.length} astronauts`,               color:'#ef5350' },
        ].map(({ icon, label, value, color }) => (
          <Grid item xs={6} sm={4} md={2} key={label}>
            <Card sx={{ border:`1px solid ${color}33`, textAlign:'center',
              background:`linear-gradient(135deg, rgba(10,22,40,0.9), rgba(5,13,26,0.95))` }}>
              <CardContent sx={{ p:1.5, '&:last-child':{ pb:1.5 } }}>
                <Box sx={{ color, mb:0.5, display:'flex', justifyContent:'center' }}>{icon}</Box>
                {loading && label === 'Latitude'
                  ? <CircularProgress size={16} sx={{ color }} />
                  : <Typography variant="body2" sx={{ fontWeight:800, color, fontSize:12 }}>{value}</Typography>
                }
                <Typography variant="caption" sx={{ color:'text.secondary', fontSize:9 }}>{label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* ── Main Layout ────────────────────────────────────────────────────── */}
      <Grid container spacing={2}>

        {/* ── Map ────────────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={8}>
          <Card sx={{
            overflow: 'hidden',
            border: '1px solid rgba(255,255,255,0.2)',
            boxShadow: '0 0 40px rgba(255,255,255,0.05)',
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            transition: 'all 0.3s ease',
          }}>
              
              {/* Fullscreen button — bottom right always visible */}
            {!isFullscreen && (
              <Box sx={{ position:'absolute', bottom:80, right:16, zIndex:9999 }}>
                <Tooltip title="Full Screen" placement="left">
                  <IconButton
                    onClick={() => {
                      const el = mapRef.current?.parentElement;
                      if (el) el.requestFullscreen?.();
                    }}
                    sx={{
                      bgcolor:'rgba(10,22,40,0.9)', color:'primary.light',
                      border:'1px solid rgba(255,255,255,0.4)',
                      width:44, height:44,
                      '&:hover':{ bgcolor:'rgba(2,136,209,0.3)' },
                    }}>
                    <Fullscreen />
                  </IconButton>
                </Tooltip>
              </Box>
            )}

                {/* Exit fullscreen button — bottom left */}
            {isFullscreen && (
              <Box sx={{ position:'absolute', bottom:80, left:16, zIndex:9999 }}>
                <Tooltip title="Exit Full Screen" placement="right">
                  <IconButton
                    onClick={() => document.exitFullscreen?.()}
                    sx={{
                      bgcolor:'rgba(239,83,80,0.2)', color:'#ef5350',
                      border:'1px solid rgba(239,83,80,0.5)',
                      width:44, height:44,
                      '&:hover':{ bgcolor:'rgba(239,83,80,0.4)' },
                    }}>
                    <FullscreenExit />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
            {/* Loading overlay */}
            {!mapLoaded && (
              <Box sx={{ position:'absolute', inset:0, zIndex:10, display:'flex',
                alignItems:'center', justifyContent:'center', bgcolor:'rgba(5,13,26,0.95)' }}>
                <Box sx={{ textAlign:'center' }}>
                  <CircularProgress sx={{ color:'primary.main', mb:2 }} />
                  <Typography variant="body2" sx={{ color:'text.secondary' }}>
                    Loading interactive map...
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Map container */}
            <Box ref={mapRef} sx={{ width: '100%', height: isFullscreen ? 0 : 500, flex: isFullscreen ? 1 : 'none', minHeight: 0 }} />

            {/* Map overlays */}
            {mapLoaded && (
              <>
                {/* Zoom controls overlay */}
                <Box sx={{ position:'absolute', top:12, right:12, zIndex:999,
                  display:'flex', flexDirection:'column', gap:0.5 }}>
                  <Tooltip title="Center on ISS" placement="left">
                    <IconButton onClick={centerOnISS} size="small"
                      sx={{ bgcolor:'rgba(10,22,40,0.9)', color:'primary.light',
                        border:'1px solid rgba(255,255,255,0.3)',
                        '&:hover':{ bgcolor:'rgba(2,136,209,0.3)' } }}>
                      <MyLocation fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>

                {/* ISS position floating badge */}
                {position && (
                  <Box sx={{ position:'absolute', top:16, left:'50%', transform:'translateX(-50%)', zIndex:999,
                    bgcolor:'rgba(5,13,26,0.88)', border:'1px solid rgba(255,255,255,0.3)',
                    borderRadius:2, px:2, py:1, backdropFilter:'blur(8px)' }}>
                    <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                      <Box sx={{ width:8, height:8, borderRadius:'50%', bgcolor:'#66bb6a',
                        animation:'pulse 1.5s infinite',
                        '@keyframes pulse':{ '0%,100%':{ opacity:1 },'50%':{ opacity:0.3 } } }} />
                      <Typography variant="caption" sx={{ color:'primary.light', fontWeight:700, fontFamily:'monospace', fontSize:12 }}>
                        {position.lat.toFixed(3)}° {position.lat >= 0 ? 'N' : 'S'} &nbsp;
                        {Math.abs(position.lon).toFixed(3)}° {position.lon >= 0 ? 'E' : 'W'}
                      </Typography>
                    </Box>
                  </Box>
                )}
              </>
            )}

            {/* Legend */}
            <Box sx={{ px:2, py:1.2, bgcolor:'rgba(5,13,26,0.8)',
              borderTop:'1px solid rgba(255,255,255,0.1)',
              display:'flex', gap:3, flexWrap:'wrap', alignItems:'center' }}>
              {[
                { color:'#ffffff', label:'ISS Position',       type:'circle' },
                { color:'#ffffff', label:'Orbit Trail',         type:'dash'   },
                { color:'#66bb6a', label:'Your Location',       type:'circle' },
              ].map(({ color, label, type }) => (
                <Box key={label} sx={{ display:'flex', alignItems:'center', gap:0.8 }}>
                  {type === 'dash'
                    ? <Box sx={{ width:18, height:2, bgcolor:color, opacity:0.7,
                        backgroundImage:`repeating-linear-gradient(90deg, ${color} 0, ${color} 4px, transparent 4px, transparent 8px)`,
                        bgcolor:'transparent' }} />
                    : <Box sx={{ width:9, height:9, borderRadius:'50%', bgcolor:color }} />
                  }
                  <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>{label}</Typography>
                </Box>
              ))}
              <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10, ml:'auto' }}>
                🗺 © OpenStreetMap contributors
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* ── Side panels ────────────────────────────────────────────────── */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ display:'flex', flexDirection:'column', gap:2 }}>

            {/* Humans in Space */}
            <Card sx={{ border:'1px solid rgba(255,255,255,0.2)',
              background:'linear-gradient(135deg, rgba(2,136,209,0.06), rgba(10,22,40,0.95))' }}>
              <CardContent sx={{ p:2.5 }}>
                <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:2 }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Person sx={{ color:'primary.main' }} />
                    <Typography variant="subtitle1" sx={{ fontWeight:700 }}>
                      Humans in Space
                    </Typography>
                  </Box>
                  <Chip label={astronauts.length} size="small"
                    sx={{ bgcolor:'rgba(255,255,255,0.15)', color:'primary.light',
                      fontWeight:800, fontSize:13, minWidth:28 }} />
                </Box>

                {astronauts.length === 0 ? (
                  <Box sx={{ display:'flex', alignItems:'center', gap:1.5, py:1 }}>
                    <CircularProgress size={20} sx={{ color:'primary.main' }} />
                    <Typography variant="body2" sx={{ color:'text.secondary' }}>
                      Fetching crew data...
                    </Typography>
                  </Box>
                ) : (
                  <>
                    {/* ISS crew */}
                    {issAstronauts.length > 0 && (
                      <>
                        <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:1 }}>
                          <Chip label="🛸 ISS" size="small"
                            sx={{ bgcolor:'rgba(255,255,255,0.1)', color:'primary.light', fontSize:10, fontWeight:700 }} />
                          <Typography variant="caption" sx={{ color:'text.secondary' }}>
                            {issAstronauts.length} aboard
                          </Typography>
                        </Box>
                        <Box sx={{ display:'flex', flexDirection:'column', gap:0.8, mb:1.5 }}>
                          {issAstronauts.map((person, i) => (
                            <Box key={i} sx={{ display:'flex', alignItems:'center', gap:1.5,
                              p:1, borderRadius:1.5,
                              bgcolor:'rgba(255,255,255,0.05)',
                              border:'1px solid rgba(255,255,255,0.1)',
                              transition:'all 0.2s',
                              '&:hover':{ bgcolor:'rgba(255,255,255,0.1)' } }}>
                              <Avatar sx={{ width:30, height:30, fontSize:12, fontWeight:800,
                                bgcolor: ASTRONAUT_COLORS[i % ASTRONAUT_COLORS.length] + '33',
                                color:   ASTRONAUT_COLORS[i % ASTRONAUT_COLORS.length],
                                border:`1px solid ${ASTRONAUT_COLORS[i % ASTRONAUT_COLORS.length]}44` }}>
                                {person.name.charAt(0)}
                              </Avatar>
                              <Box sx={{ flex:1 }}>
                                <Typography variant="body2" sx={{ fontWeight:700, color:'text.primary', fontSize:12 }}>
                                  {person.name}
                                </Typography>
                                <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>
                                  🛸 ISS
                                </Typography>
                              </Box>
                              <Box sx={{ width:6, height:6, borderRadius:'50%', bgcolor:'#66bb6a',
                                animation:'pulse 2s infinite',
                                '@keyframes pulse':{ '0%,100%':{ opacity:1 },'50%':{ opacity:0.3 } } }} />
                            </Box>
                          ))}
                        </Box>
                      </>
                    )}

                    {/* Other spacecraft */}
                    {otherCraft.map(craft => {
                      const crew = astronauts.filter(a => a.craft === craft);
                      return (
                        <Box key={craft}>
                          <Divider sx={{ borderColor:'rgba(255,255,255,0.08)', my:1 }} />
                          <Chip label={`🚀 ${craft}`} size="small"
                            sx={{ bgcolor:'rgba(255,167,38,0.1)', color:'#ffa726', fontSize:10, fontWeight:700, mb:1 }} />
                          {crew.map((p, i) => (
                            <Box key={i} sx={{ display:'flex', alignItems:'center', gap:1, py:0.5 }}>
                              <Avatar sx={{ width:24, height:24, fontSize:10, bgcolor:'rgba(255,167,38,0.2)', color:'#ffa726' }}>
                                {p.name.charAt(0)}
                              </Avatar>
                              <Typography variant="caption" sx={{ color:'text.secondary' }}>{p.name}</Typography>
                            </Box>
                          ))}
                        </Box>
                      );
                    })}
                  </>
                )}
              </CardContent>
            </Card>

            {/* ISS Pass Times */}
            <Card sx={{ border:'1px solid rgba(255,255,255,0.15)' }}>
              <CardContent sx={{ p:2.5 }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1, mb:2 }}>
                  <AccessTime sx={{ color:'primary.main' }} />
                  <Typography variant="subtitle1" sx={{ fontWeight:700 }}>
                    Next Passes
                  </Typography>
                  {userLocation && (
                    <Chip label="Near You" size="small"
                      sx={{ bgcolor:'rgba(102,187,106,0.15)', color:'#66bb6a', fontSize:10, fontWeight:700 }} />
                  )}
                </Box>

                {!userLocation ? (
                  <Box sx={{ textAlign:'center', py:2 }}>
                    <MyLocation sx={{ fontSize:32, opacity:0.2, mb:1 }} />
                    <Typography variant="body2" sx={{ color:'text.secondary', fontSize:12 }}>
                      Enable location to see ISS pass times
                    </Typography>
                  </Box>
                ) : passes.length === 0 ? (
                  <CircularProgress size={20} sx={{ color:'primary.main' }} />
                ) : (
                  passes.map((pass, i) => (
                    <Box key={i} sx={{ display:'flex', alignItems:'center', justifyContent:'space-between',
                      p:1.2, mb:0.8, borderRadius:1.5,
                      bgcolor: i === 0 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)',
                      border:  i === 0 ? '1px solid rgba(255,255,255,0.2)' : '1px solid rgba(255,255,255,0.04)' }}>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight:700,
                          color: i === 0 ? 'primary.light' : 'text.primary', fontSize:12 }}>
                          {i === 0 ? '⭐ Next' : `Pass ${i+1}`}
                        </Typography>
                        <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>
                          {pass.riseTime_formatted}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign:'right' }}>
                        <Chip label={`${Math.floor(pass.duration/60)}m ${pass.duration%60}s`}
                          size="small"
                          sx={{ bgcolor:'rgba(255,255,255,0.1)', color:'primary.light', fontSize:9, fontWeight:700, height:18 }} />
                        <Typography variant="caption" sx={{ color:'text.secondary', display:'block', mt:0.2, fontSize:10 }}>
                          Max: {pass.maxElevation}
                        </Typography>
                      </Box>
                    </Box>
                  ))
                )}

                <Box sx={{ mt:1.5, p:1.2, borderRadius:1.5,
                  bgcolor:'rgba(255,167,38,0.05)', border:'1px solid rgba(255,167,38,0.15)' }}>
                  <Typography variant="caption" sx={{ color:'#ffa726', fontSize:10 }}>
                    💡 ISS is visible to the naked eye at dusk or dawn — look for a bright fast-moving dot.
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          </Box>
        </Grid>

        {/* ── ISS Fast Facts ─────────────────────────────────────────────── */}
        <Grid item xs={12}>
          <Card sx={{ border:'1px solid rgba(255,255,255,0.12)' }}>
            <CardContent sx={{ p:2.5 }}>
              <Typography variant="subtitle1" sx={{ fontWeight:700, mb:2 }}>
                🛸 ISS Fast Facts
              </Typography>
              <Grid container spacing={1.5}>
                {[
                  { label:'Length',             value:'109 meters' },
                  { label:'Mass',               value:'420,000 kg' },
                  { label:'Orbits per day',      value:'~15.5' },
                  { label:'In operation since',  value:'Nov 2, 2000' },
                  { label:'Total crew members',  value:'270+' },
                  { label:'Experiments done',    value:'3,000+' },
                  { label:'Countries involved',  value:'15' },
                  { label:'Pressurized volume',  value:'915 m³' },
                ].map(({ label, value }) => (
                  <Grid item xs={6} sm={3} key={label}>
                    <Box sx={{ p:1.5, borderRadius:2, textAlign:'center',
                      bgcolor:'rgba(255,255,255,0.04)', border:'1px solid rgba(255,255,255,0.1)',
                      transition:'all 0.2s',
                      '&:hover':{ bgcolor:'rgba(255,255,255,0.08)', border:'1px solid rgba(255,255,255,0.2)' } }}>
                      <Typography variant="body2" sx={{ fontWeight:800, color:'primary.light' }}>
                        {value}
                      </Typography>
                      <Typography variant="caption" sx={{ color:'text.secondary', fontSize:10 }}>
                        {label}
                      </Typography>
                    </Box>
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

      </Grid>
    </Box>
  );
}
